import {
  BellOutlined,
  DatabaseOutlined,
  HomeOutlined,
  KeyOutlined,
  LogoutOutlined,
  ProjectOutlined,
  ReadOutlined,
  RiseOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Breadcrumb, Button, Divider, Layout, Menu, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProjectAlerts } from '../alerts/useProjectAlerts';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';
import { BrandMark } from './BrandMark';

/** Một mục của thanh điều hướng. Mục có <c>children</c> là một nhóm mở ra được. */
interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly children?: NavItem[];
}

/** Mục đang mở và nhóm chứa nó — rỗng khi mục nằm ở cấp cao nhất. */
interface ActiveNavItem {
  readonly item: NavItem;
  readonly group: NavItem | null;
}

/**
 * Mục điều hướng ứng với đường dẫn đang mở, kèm nhóm chứa nó.
 *
 * Khớp đúng đường dẫn trước; không có thì lấy mục có đường dẫn là phần đầu DÀI NHẤT. Nhờ vậy trang
 * chi tiết (<c>/du-an/…</c>), trang viết bài (<c>/blog/viet-bai</c>) vẫn sáng mục cha của nó, và
 * <c>/du-an/bang</c> sáng đúng "Bảng tiến độ" chứ không sáng "Danh sách dự án".
 *
 * Trang chủ chỉ khớp đúng <c>/</c> — mọi đường dẫn đều bắt đầu bằng dấu gạch chéo.
 */
function findActiveItem(
  items: readonly NavItem[],
  pathname: string,
): ActiveNavItem | null {
  const candidates = items.flatMap<ActiveNavItem>((item) =>
    item.children === undefined
      ? [{ item, group: null }]
      : item.children.map((child) => ({ item: child, group: item })),
  );

  let best: ActiveNavItem | null = null;

  for (const candidate of candidates) {
    const { key } = candidate.item;
    const isMatch = key === pathname || (key !== AppRoutes.Home && pathname.startsWith(`${key}/`));

    if (isMatch && (best === null || key.length > best.item.key.length)) {
      best = candidate;
    }
  }

  return best;
}

/** Một mục theo kiểu thanh điều hướng của thư viện thành phần. */
type MenuItem = Required<MenuProps>['items'][number];

/** Đổi cây mục điều hướng sang kiểu của thư viện — nhóm phải có danh sách con, mục lá thì không. */
function toMenuItems(items: readonly NavItem[]): MenuItem[] {
  return items.map((item) =>
    item.children === undefined
      ? { key: item.key, label: item.label, icon: item.icon }
      : { key: item.key, label: item.label, icon: item.icon, children: toMenuItems(item.children) },
  );
}

/** Chữ cái đầu của TÊN (từ cuối cùng trong họ tên tiếng Việt), để làm ảnh đại diện. */
function initialOf(fullName: string | undefined): string {
  const lastWord = fullName?.trim().split(/\s+/).at(-1) ?? '';

  return lastWord === '' ? '?' : lastWord.charAt(0).toLocaleUpperCase('vi-VN');
}

/**
 * Khung bố cục chung của hệ thống.
 *
 * Theo mẫu bố cục đã chọn ở tài liệu thiết kế giao diện: thanh điều hướng dọc bên trái, thanh
 * công cụ trên cùng, vùng nội dung ở giữa. Mẫu này quen với người đã dùng phần mềm quản trị
 * doanh nghiệp, và đó là tiêu chí quan trọng hơn vẻ ngoài.
 *
 * Vẻ ngoài theo quy định hình ảnh thương hiệu MVA Lab: nền trắng và sáng, xanh dương là màu nhận
 * diện của mục đang chọn, đen than cho chữ, gold chỉ ở chi tiết nhỏ.
 *
 * Các mục được ẩn hiện theo quyền của người dùng. Đây KHÔNG phải một lớp bảo vệ — máy chủ kiểm tra
 * lại quyền ở từng điểm cuối. Ẩn mục chỉ để người dùng không bấm vào một trang rồi nhận thông báo
 * từ chối.
 */
export function AppLayout({ children }: { readonly children: ReactNode }): ReactNode {
  const { user, signOut, hasPermission } = useAuth();
  const { summary } = useProjectAlerts();

  const navigate = useNavigate();
  const location = useLocation();

  const [isSigningOut, setIsSigningOut] = useState(false);

  /*
    Hai mục quản trị, mỗi mục ẩn hiện theo quyền riêng của nó. Nhóm biến mất khi không còn mục nào —
    để nhóm rỗng hiện ra sẽ cho người dùng bấm vào một mục không mở ra được gì.
  */
  const administrationChildren: NavItem[] = [
    ...(hasPermission(PermissionCodes.UserAccountView)
      ? [{ key: AppRoutes.UserAccount, label: 'Tài khoản người dùng' }]
      : []),
    ...(hasPermission(PermissionCodes.RoleView)
      ? [{ key: AppRoutes.Role, label: 'Vai trò và phân quyền' }]
      : []),
  ];

  /*
    Các mục của module dự án. Bảng tiến độ là CÁCH NHÌN khác của cùng dữ liệu danh sách, gom theo
    trạng thái, nên nó nằm cùng nhóm chứ không tách thành mục riêng ở cấp cao nhất.

    Bảng phân bổ nguồn lực đi theo quyền xem thành viên, không theo quyền xem dự án: nó trả lời câu
    hỏi về con người chứ không về dự án nào.
  */
  const projectChildren: NavItem[] = [
    /*
      "Việc của tôi" đứng ĐẦU nhóm: đây là màn hình người làm mở đầu tiên trong ngày, còn danh sách
      dự án là thứ người quản lý mở. Ở công ty nhỏ thì số người làm nhiều hơn số người quản lý.
    */
    ...(hasPermission(PermissionCodes.ProjectTaskView)
      ? [{ key: AppRoutes.MyTasks, label: 'Việc của tôi' }]
      : []),
    ...(hasPermission(PermissionCodes.ProjectView)
      ? [
          { key: AppRoutes.Project, label: 'Danh sách dự án' },
          { key: AppRoutes.ProjectBoard, label: 'Bảng tiến độ' },
        ]
      : []),
    ...(hasPermission(PermissionCodes.ProjectMemberView)
      ? [{ key: AppRoutes.ProjectWorkload, label: 'Phân bổ nguồn lực' }]
      : []),
    /*
      Mẫu dự án nằm trong nhóm dự án chứ không nằm trong danh mục: nó là quy trình chuẩn của công ty,
      và người mở nó là người lập kế hoạch — cùng nhóm người mở những mục còn lại ở đây.
    */
    ...(hasPermission(PermissionCodes.ProjectTemplateView)
      ? [{ key: AppRoutes.ProjectTemplate, label: 'Mẫu dự án' }]
      : []),
    // Nội dung trang Dự án tiêu biểu trên website công ty — đứng cuối nhóm vì nó không phải việc vận
    // hành dự án hằng ngày.
    ...(hasPermission(PermissionCodes.ShowcaseProjectView)
      ? [{ key: AppRoutes.ShowcaseProject, label: 'Demo dự án' }]
      : []),
  ];

  /*
    Nhóm khách hàng. "Liên hệ khách hàng" đứng TRƯỚC: đó là những người vừa để lại thông tin trên
    website và đang chờ được gọi lại — việc gấp hơn tra cứu khách hàng đã có.
  */
  const customerChildren: NavItem[] = [
    ...(hasPermission(PermissionCodes.ContactRequestView)
      ? [{ key: AppRoutes.ContactRequest, label: 'Liên hệ khách hàng' }]
      : []),
    ...(hasPermission(PermissionCodes.CustomerView)
      ? [{ key: AppRoutes.Customer, label: 'Đối tác' }]
      : []),
  ];

  const catalogChildren: NavItem[] = [
    ...(hasPermission(PermissionCodes.UnitOfMeasureView)
      ? [{ key: AppRoutes.UnitOfMeasure, label: 'Đơn vị đo' }]
      : []),
    ...(hasPermission(PermissionCodes.CurrencyView)
      ? [{ key: AppRoutes.Currency, label: 'Tiền tệ' }]
      : []),
    ...(hasPermission(PermissionCodes.CountryView)
      ? [{ key: AppRoutes.Country, label: 'Quốc gia' }]
      : []),
    ...(hasPermission(PermissionCodes.TaxRateView)
      ? [{ key: AppRoutes.TaxRate, label: 'Thuế suất' }]
      : []),
    ...(hasPermission(PermissionCodes.ProjectTypeView)
      ? [{ key: AppRoutes.ProjectType, label: 'Loại dự án' }]
      : []),
  ];

  const navigationItems: NavItem[] = [
    { key: AppRoutes.Home, icon: <HomeOutlined />, label: 'Trang chủ' },

    /*
      Dự án đứng TRÊN khách hàng: đây là mục tiêu cốt lõi số một, và là màn hình người dùng mở nhiều
      nhất trong ngày. Khách hàng thì phần lớn chỉ mở khi khai thêm một khách mới hoặc tra lại thông
      tin hóa đơn.
    */
    ...(projectChildren.length === 0
      ? []
      : [{ key: 'du-an', icon: <ProjectOutlined />, label: 'Dự án', children: projectChildren }]),

    /*
      Bán hàng đứng trên Danh mục: người dùng vào hệ thống để làm việc với khách hàng, còn danh mục
      là thứ họ chỉ mở khi cần khai thêm một giá trị còn thiếu.
    */
    ...(hasPermission(PermissionCodes.OpportunityView)
      ? [{ key: AppRoutes.Opportunity, icon: <RiseOutlined />, label: 'Cơ hội bán hàng' }]
      : []),
    ...(customerChildren.length === 0
      ? []
      : [{ key: 'khach-hang', icon: <TeamOutlined />, label: 'Khách hàng', children: customerChildren }]),

    // Blog của website công ty — nội dung công khai, không thuộc vận hành dự án hằng ngày.
    ...(hasPermission(PermissionCodes.BlogView)
      ? [{ key: AppRoutes.Blog, icon: <ReadOutlined />, label: 'Blog' }]
      : []),

    ...(catalogChildren.length === 0
      ? []
      : [{ key: 'danh-muc', icon: <DatabaseOutlined />, label: 'Danh mục', children: catalogChildren }]),

    /*
      Quản trị nằm dưới cùng, trên mục đổi mật khẩu: đây là việc làm thưa — lập tài khoản cho người
      mới, đổi vai trò khi có người chuyển bộ phận — không phải việc hằng ngày.

      Gom thành một nhóm vì hai mục này luôn đi cùng nhau: lập tài khoản xong là phải gán vai trò, và
      lập vai trò xong là phải cấp quyền rồi khai phạm vi dữ liệu.
    */
    ...(administrationChildren.length === 0
      ? []
      : [
          {
            key: 'quan-tri',
            icon: <SettingOutlined />,
            label: 'Quản trị',
            children: administrationChildren,
          },
        ]),

    { key: AppRoutes.ChangePassword, icon: <KeyOutlined />, label: 'Đổi mật khẩu' },
  ];

  const active = findActiveItem(navigationItems, location.pathname);
  const activeGroupKey = active?.group?.key ?? null;

  /*
    Nhóm nào đang mở. Mở web ở trang chủ thì mọi nhóm đều thu gọn — người dùng bấm vào nhóm nào mới
    sổ nhóm đó ra. Một danh sách dài bung sẵn bắt mắt phải dò cả cột mới thấy thứ mình cần.

    Ngoại lệ duy nhất: trang đang mở nằm trong một nhóm (vào thẳng bằng đường dẫn, tải lại trang,
    hay đi tới từ một liên kết trong nội dung) thì nhóm đó tự mở, để mục đang sáng không bị giấu.
    Chỉ mở thêm khi đổi sang nhóm khác — người dùng tự thu nhóm hiện tại lại thì để yên.
  */
  const [openKeys, setOpenKeys] = useState<string[]>(
    activeGroupKey === null ? [] : [activeGroupKey],
  );
  const [openedForGroupKey, setOpenedForGroupKey] = useState(activeGroupKey);

  if (activeGroupKey !== openedForGroupKey) {
    setOpenedForGroupKey(activeGroupKey);

    if (activeGroupKey !== null && !openKeys.includes(activeGroupKey)) {
      setOpenKeys([...openKeys, activeGroupKey]);
    }
  }

  async function handleSignOut(): Promise<void> {
    setIsSigningOut(true);

    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider
        className="erp-sider"
        theme="light"
        breakpoint="lg"
        collapsedWidth={0}
        width={240}
      >
        <div className="erp-sider-brand">
          <BrandMark />
        </div>

        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[active?.item.key ?? location.pathname]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onClick={({ key }) => {
            void navigate(key);
          }}
          items={toMenuItems(navigationItems)}
        />
      </Layout.Sider>

      <Layout style={{ minWidth: 0 }}>
        <Layout.Header className="erp-header">
          {/*
            Đường dẫn của trang đang mở: nhóm › mục. Chỉ để định hướng — người dùng mở trang chi tiết
            từ một liên kết vẫn biết mình đang đứng trong phần nào của hệ thống.
          */}
          <div style={{ minWidth: 0 }}>
            {active === null ? null : (
              <Breadcrumb
                items={[
                  ...(active.group === null ? [] : [{ title: active.group.label }]),
                  { title: active.item.label },
                ]}
              />
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {/*
              Chuông chỉ hiện khi đã nạp được cảnh báo. Hiện một chuông trống với người không có
              quyền xem công việc là mời họ bấm vào thứ không mở ra được gì.

              Số trên chuông là TỔNG số dòng cảnh báo, không phải tổng số việc: một dòng "bạn có 20
              việc quá hạn" đếm là một. Đếm 20 sẽ cho một con số to đến mức không ai xử lý nổi, và
              con số không xử lý nổi thì bị bỏ qua.

              Màu đỏ dành riêng cho cảnh báo mức cần xử lý ngay. Nếu mọi thứ đều đỏ thì không có gì
              là đỏ nữa. Mức còn lại dùng gold đậm — gold gốc quá nhạt để làm nền cho chữ trắng.
            */}
            {summary === null ? null : (
              <Tooltip
                title={
                  summary.totalCount === 0
                    ? 'Không có việc nào cần chú ý'
                    : 'Mở trang chủ để xem những việc cần chú ý'
                }
              >
                <Badge
                  count={summary.totalCount}
                  color={summary.criticalCount > 0 ? BrandColors.red : BrandColors.goldDeep}
                  size="small"
                  offset={[-4, 4]}
                >
                  <Button
                    type="text"
                    shape="circle"
                    aria-label="Những việc cần chú ý"
                    icon={<BellOutlined style={{ fontSize: 18 }} />}
                    onClick={() => {
                      void navigate(AppRoutes.Home);
                    }}
                  />
                </Badge>
              </Tooltip>
            )}

            <Divider type="vertical" style={{ height: 24, marginInline: 4 }} />

            <span className="erp-user-chip">
              <Avatar
                size={34}
                style={{ background: BrandColors.blue, fontWeight: 600, flexShrink: 0 }}
              >
                {initialOf(user?.fullName)}
              </Avatar>
              <Typography.Text strong className="erp-hide-sm" style={{ maxWidth: 200 }} ellipsis>
                {user?.fullName}
              </Typography.Text>
            </span>

            <Tooltip title="Đăng xuất">
              <Button
                type="text"
                icon={<LogoutOutlined />}
                loading={isSigningOut}
                onClick={() => {
                  void handleSignOut();
                }}
              >
                <span className="erp-hide-sm">Đăng xuất</span>
              </Button>
            </Tooltip>
          </div>
        </Layout.Header>

        <Layout.Content className="erp-content">{children}</Layout.Content>
      </Layout>
    </Layout>
  );
}
